import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMessage } from 'naive-ui'
import { useSourceActivityStore, type SourceRow } from '../stores/source_activity'

// `lastCompleted` is exposed as a reactive ref because that's the
// cheapest way for the store to surface "an in-flight row just landed
// in terminal status." Consumers want event semantics, not state, so
// this composable holds the skip-first-emission flag privately and
// turns the ref-change into a single toast per terminal transition.
//
// Skip-first matters: when ActionCable reconnects, the store re-runs
// load() to backfill rows that broadcast during the disconnect window.
// The first emission after subscribe() can therefore reflect a stale
// transition the user has already seen. Suppressing it keeps the toast
// a fresh-event signal rather than a "you have unread state" badge.
//
// Requires NMessageProvider in the tree (App.vue mounts one).
export function useIngestCompletionToast(): void {
  const store = useSourceActivityStore()
  const { lastCompleted } = storeToRefs(store)
  const message = useMessage()
  let firstEmission = true

  watch(lastCompleted, (row) => {
    if (!row) return
    if (firstEmission) {
      firstEmission = false
      return
    }
    const label = ingestLabel(row)
    if (row.status === 'succeeded') {
      message.success(`Ingest finished: ${label}`)
    } else if (row.status === 'failed') {
      message.error(`Ingest failed: ${label}${row.error ? ` — ${row.error}` : ''}`)
    }
  })
}

// Single-line max for the toast label — long enough to identify the
// prompt at a glance, short enough to fit the toast on a 390px mobile
// viewport without wrapping into a multi-line stack.
const LABEL_MAX_CHARS = 60

// Exported for the spec — stays a pure function over the SourceRow
// shape so it can be tested without mocking useMessage.
export function ingestLabel(row: Pick<SourceRow, 'kind' | 'content' | 'url'>): string {
  if (row.kind === 'url') return row.url || 'URL'
  if (row.kind === 'file_upload') return row.content || 'file'
  const trimmed = (row.content || '').trim()
  if (!trimmed) return 'prompt'
  return trimmed.length > LABEL_MAX_CHARS ? `${trimmed.slice(0, LABEL_MAX_CHARS - 1).trimEnd()}…` : trimmed
}
